import React, { Suspense } from "react";
import ErrorBoundary from "@/shared/widgets/ErrorBoundary";
import { useModalContext } from "@/shared/contexts/modal.context";
import { ModalBackdrop } from "./ModalBackdrop";
import {
  getModal,
  hasModal,
  ModalName,
  getAvailableModals,
} from "@/shared/modals";

export interface ModalLoaderProps {
  onClose: (stackProps?: { [key: string]: any }) => void;
  onUpdate?: (
    meta: { [key: string]: any },
    modalStackProps?: { [key: string]: any }
  ) => void;
  filename: string;
  stackSize?: number;
  [key: string]: any;
}

function ModalLoader({ filename, ...rest }: ModalLoaderProps) {
  const { state, updateModal } = useModalContext();
  const modalStackLength = state.modalStack.length;
  const { modalStackProps } = state;

  // Validate modal exists in registry
  if (!hasModal(filename)) {
    const availableModals = getAvailableModals().join(", ");
    console.error(
      `Modal "${filename}" not found in registry. Available modals: ${availableModals}`
    );
    return null;
  }

  // Get the lazy-loaded modal component from registry
  const Component = getModal(filename as ModalName);

  const handleUpdate = (
    meta: { [key: string]: any },
    modalStackProps?: { [key: string]: any }
  ) => {
    updateModal(filename, meta, modalStackProps);
  };

  return (
    <Suspense fallback={null}>
      <ErrorBoundary>
        <ModalBackdrop onClose={rest?.onClose}>
          {filename ? (
            <Component
              {...rest}
              {...modalStackProps}
              onUpdate={handleUpdate}
              stackSize={modalStackLength || 1}
            />
          ) : null}
        </ModalBackdrop>
      </ErrorBoundary>
    </Suspense>
  );
}

export { ModalLoader };
